import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react'
import {
  GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, User,
} from 'firebase/auth'
import { firebaseAuth } from './firebase'

// Mirrors app/js/auth.js: tenant comes from /api/auth/me after login. The
// active tenant for super-admins is stored in sessionStorage under the key
// 'activeTenant' (matching vanilla so both UIs share state in the same tab).
export interface MeResponse {
  email: string
  tenant_id: string | null
  is_super_admin: boolean
  available_tenants?: string[]
  display_name?: string
  features?: Record<string, unknown>
}

interface TenantListItem { tenant_id: string; display_name?: string }

interface AuthState {
  user: User | null
  me: MeResponse | null
  loading: boolean
  activeTenant: string | null  // super-admin override OR me.tenant_id
  availableTenants: string[]   // for the super-admin tenant picker dropdown
  setActiveTenant: (t: string) => void
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

// Vanilla auth.js stores tenant in sessionStorage under 'activeTenant'.
// Using the same key lets users switch between vanilla + React in the same
// tab without losing tenant selection.
const ACTIVE_TENANT_KEY = 'activeTenant'

const AuthCtx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTenant, setActiveTenantState] = useState<string | null>(
    () => sessionStorage.getItem(ACTIVE_TENANT_KEY)
  )
  const [availableTenants, setAvailableTenants] = useState<string[]>([])

  // Centralised setter — always writes through to sessionStorage so the
  // next authFetch sees it without waiting for a re-render.
  const setActiveTenant = (t: string) => {
    sessionStorage.setItem(ACTIVE_TENANT_KEY, t)
    setActiveTenantState(t)
  }

  // Keep sessionStorage (what authFetch reads for X-Tenant-ID) in sync with the
  // tenant the UI actually shows. The context resolves activeTenant as
  // `state || me.tenant_id`, but authFetch reads sessionStorage with NO
  // me.tenant_id fallback — so a super-admin whose active tenant comes from that
  // fallback would have the nav on one tenant while API calls sent no header and
  // returned home-tenant data. Syncing closes that gap for EVERY page. We never
  // write when the resolved tenant is null (super-admins with no pick still get
  // routed to /admin), so the "explicitly choose a tenant" flow is preserved.
  useEffect(() => {
    const resolved = activeTenant || me?.tenant_id
    if (resolved && sessionStorage.getItem(ACTIVE_TENANT_KEY) !== resolved) {
      sessionStorage.setItem(ACTIVE_TENANT_KEY, resolved)
    }
  }, [activeTenant, me])

  // Vanilla auth.js refreshes the Firebase ID token every 50 min (tokens
  // expire at 60 min). Without this, long-running React tabs go 401 on every
  // /api/* call after an hour. Setup runs once a user is signed in.
  useEffect(() => {
    if (!user) return
    const id = window.setInterval(async () => {
      try {
        await user.getIdToken(true)  // force refresh
      } catch (e) {
        console.warn('Token refresh failed:', e)
      }
    }, 50 * 60 * 1000)
    return () => window.clearInterval(id)
  }, [user])

  useEffect(() => {
    return onAuthStateChanged(firebaseAuth, async (u) => {
      setUser(u)
      if (!u) { setMe(null); setLoading(false); return }
      try {
        const token = await u.getIdToken()
        // First call — no tenant header yet; server returns base identity
        // (tenant_id might be null for super admins until they pick one).
        const r = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (r.ok) {
          const meData: MeResponse = await r.json()
          setMe(meData)

          // For super admins, populate availableTenants for the picker UI but
          // DO NOT auto-pick. Vanilla flow (login.html:473): sign-in routes
          // super admins to /admin.html to explicitly pick a tenant first.
          // Auto-picking caused users to land on the wrong tenant's data.
          if (meData.is_super_admin) {
            try {
              const tr = await fetch('/api/admin/tenants', {
                headers: { Authorization: `Bearer ${token}` },
              })
              if (tr.ok) {
                const td = await tr.json()
                const tenants: TenantListItem[] = td.tenants || []
                setAvailableTenants(tenants.map((t) => t.tenant_id))
              }
            } catch (e) {
              console.warn('Failed to fetch /api/admin/tenants:', e)
            }
          } else if (meData.tenant_id && !sessionStorage.getItem(ACTIVE_TENANT_KEY)) {
            // Regular user — seed from /me (they can only ever see one tenant).
            setActiveTenant(meData.tenant_id)
          }
        }
      } catch (e) {
        console.warn('Failed to fetch /api/auth/me:', e)
      } finally {
        setLoading(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo<AuthState>(() => ({
    user, me, loading,
    activeTenant: activeTenant || me?.tenant_id || null,
    availableTenants: me?.available_tenants || availableTenants,
    setActiveTenant,
    signIn: async () => {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ hd: '9fs.ai' })
      await signInWithPopup(firebaseAuth, provider)
    },
    signOut: async () => {
      sessionStorage.removeItem(ACTIVE_TENANT_KEY)
      setActiveTenantState(null)
      await signOut(firebaseAuth)
    },
  }), [user, me, loading, activeTenant, availableTenants])

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
